import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  appConfig,
  catalog,
  catalogAdmin,
  ApiError,
  type CatalogAdvFilter,
  type CatalogAdvFilterOp,
  type CatalogItem,
  type CatalogSavedViewFilters,
  type Series,
} from '../api/index.js';
import { Card } from '../components/ui/card.js';
import { Badge, statusBadge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import {
  Search,
  AlertTriangle,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Loader2,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  RefreshCw,
  Bookmark,
  Trash2,
  Layers,
  Plus,
  X,
  Download,
  FilterX,
} from 'lucide-react';
import { SearchableSelect } from '../components/SearchableSelect.js';
import {
  CATALOG_ADV_ENUM_OPTIONS,
  CATALOG_ADV_FIELD_ID_SET,
  CATALOG_ADV_FIELD_OPTIONS,
  CATALOG_ADV_OP_OPTIONS,
} from '../catalogAdvancedFilterMeta.js';
import { useMe } from '../hooks/useAuth.js';

const STATUS_OPTIONS = ['all', 'draft', 'published', 'archived'];

const CATALOG_STATUS_OPTIONS = STATUS_OPTIONS.map((s) => ({
  value: s,
  label: s === 'all' ? 'All statuses' : s.charAt(0).toUpperCase() + s.slice(1),
}));

type SortCol = 'name' | 'status' | 'releaseAt' | 'preorderReleaseAt' | 'flags';

const AT_RISK_LABELS: Record<string, { label: string; color: string }> = {
  release_imminent_draft: { label: 'Release imminent — draft', color: 'bg-red-100 text-red-700' },
  releasing_soon_draft: { label: 'Releasing soon', color: 'bg-yellow-100 text-yellow-700' },
  missing_publisher: { label: 'Missing internal publisher', color: 'bg-orange-100 text-orange-700' },
  publisher_backend_mismatch: { label: 'Publisher/backend publisher mismatch', color: 'bg-purple-100 text-purple-700' },
  missing_description: { label: 'Missing description', color: 'bg-orange-100 text-orange-700' },
  missing_genre: { label: 'Missing genre', color: 'bg-orange-100 text-orange-700' },
  no_contributors: { label: 'No contributors', color: 'bg-orange-100 text-orange-700' },
  missing_image: { label: 'Missing cover image', color: 'bg-orange-100 text-orange-700' },
  missing_audio: { label: 'Missing audio', color: 'bg-orange-100 text-orange-700' },
  partial_audio: { label: 'Some audio incomplete', color: 'bg-yellow-100 text-yellow-700' },
  missing_sample: { label: 'Missing sample clip', color: 'bg-orange-100 text-orange-700' },
  publisher_series_mismatch: { label: 'Publisher mismatch with series', color: 'bg-purple-100 text-purple-700' },
  format_mismatch: { label: 'Format issues', color: 'bg-pink-100 text-pink-700' },
  group_formats_empty: { label: 'Group formats empty', color: 'bg-pink-100 text-pink-700' },
};

const AUDIT_FLAG_IDS = Object.keys(AT_RISK_LABELS);

function parseAuditFlagsFromSearch(params: URLSearchParams): string[] {
  const raw = params.get('flags');
  if (!raw?.trim()) return [];
  return raw.split(',').map((s) => s.trim()).filter((id) => id in AT_RISK_LABELS);
}

type AdvUiRow = { key: string } & CatalogAdvFilter;

function createAdvRow(partial?: Partial<CatalogAdvFilter> & { key?: string }): AdvUiRow {
  const key =
    partial?.key ??
    (typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `adv-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`);
  return {
    key,
    field: partial?.field ?? '',
    op: (partial?.op as CatalogAdvFilterOp) ?? 'contains',
    value: partial?.value ?? '',
  };
}

function parseAdvFiltersFromEncoded(raw: string | null): CatalogAdvFilter[] {
  if (!raw?.trim()) return [];
  let json: unknown;
  try {
    json = JSON.parse(decodeURIComponent(raw));
  } catch {
    try {
      json = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(json)) return [];
  const out: CatalogAdvFilter[] = [];
  for (const row of json.slice(0, 12)) {
    if (!row || typeof row !== 'object') continue;
    const o = row as Record<string, unknown>;
    const field = typeof o.field === 'string' ? o.field.trim() : '';
    const op = typeof o.op === 'string' ? o.op.trim() : '';
    const value = typeof o.value === 'string' ? o.value : String(o.value ?? '');
    if (!CATALOG_ADV_FIELD_ID_SET.has(field) || !CATALOG_ADV_OP_OPTIONS.some((x) => x.id === op)) continue;
    out.push({ field, op: op as CatalogAdvFilterOp, value });
  }
  return out;
}

function advRowsFromSearchParams(params: URLSearchParams): AdvUiRow[] {
  const parsed = parseAdvFiltersFromEncoded(params.get('adv'));
  if (parsed.length) return parsed.map((p) => createAdvRow(p));
  const mf = params.get('missingField')?.trim();
  if (mf) {
    const field = mf === 'series' ? 'series_id' : mf === 'group' ? 'group_id' : mf;
    if (CATALOG_ADV_FIELD_ID_SET.has(field)) return [createAdvRow({ field, op: 'empty', value: '' })];
  }
  return [];
}

function toValidAdvPayload(rows: AdvUiRow[]): CatalogAdvFilter[] {
  return rows
    .filter(
      (r) =>
        r.field &&
        CATALOG_ADV_FIELD_ID_SET.has(r.field) &&
        CATALOG_ADV_OP_OPTIONS.some((o) => o.id === r.op),
    )
    .map((r) => ({ field: r.field, op: r.op, value: r.value }));
}

function AtRiskBadge({ flags }: { flags: string[] }) {
  if (!flags?.length) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {flags.map((flag) => {
        const info = AT_RISK_LABELS[flag];
        if (!info) return null;
        return (
          <span
            key={flag}
            className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${info.color}`}
          >
            <AlertTriangle size={10} />
            {info.label}
          </span>
        );
      })}
    </div>
  );
}

const SENTINEL = 'ig.field.type.delete';

function formatItemDate(v: unknown): string {
  if (v === undefined || v === null || v === SENTINEL) return '—';
  try {
    const d = new Date(String(v));
    return isNaN(d.getTime()) ? '—' : format(d, 'MMM d, yyyy');
  } catch {
    return '—';
  }
}

function normalizeOid(v: unknown): string {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if (typeof o.$oid === 'string') return o.$oid;
    if (typeof o._id === 'string') return o._id;
  }
  return '';
}

function seriesLabel(item: CatalogItem, seriesById: Map<string, string>): string {
  // Try populated series object (Iglu sometimes embeds the full series doc)
  const raw = item.series as unknown;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    const n = o.name ?? o.title;
    if (typeof n === 'string' && n.trim()) return n.trim();
  }
  if (Array.isArray(raw) && raw[0] && typeof raw[0] === 'object') {
    const n = (raw[0] as Record<string, unknown>).name;
    if (typeof n === 'string' && n.trim()) return n.trim();
  }
  // Normalize seriesId — Iglu may return {"$oid":"..."} even if backend tries to normalize
  const rawSid = item.seriesId as unknown;
  const sid = typeof rawSid === 'string' ? rawSid : normalizeOid(rawSid);
  if (sid && sid !== SENTINEL) {
    return seriesById.get(sid) ?? '—';
  }
  return '—';
}

function SortTh({
  label,
  column,
  sortBy,
  sortDir,
  onSort,
}: {
  label: string;
  column: SortCol;
  sortBy: SortCol;
  sortDir: 'asc' | 'desc';
  onSort: (col: SortCol) => void;
}) {
  const active = sortBy === column;
  return (
    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onSort(column);
        }}
        className="inline-flex items-center gap-1 hover:text-gray-800 transition-colors"
      >
        {label}
        {active ? (
          sortDir === 'asc' ? (
            <ChevronUp size={14} className="text-brand-600" />
          ) : (
            <ChevronDown size={14} className="text-brand-600" />
          )
        ) : (
          <ArrowUpDown size={14} className="opacity-35" />
        )}
      </button>
    </th>
  );
}

export function Catalog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: me } = useMe();

  const syncMutation = useMutation({
    mutationFn: catalog.sync,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog-items'] });
      queryClient.invalidateQueries({ queryKey: ['catalog-series'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (e) => {
      setInlineError(e instanceof ApiError ? e.message : 'Sync failed — check server logs.');
    },
  });

  const [exportBusy, setExportBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState(searchParams.get('search') ?? '');
  const [search, setSearch] = useState(searchParams.get('search') ?? '');

  // Debounce search — 400ms after user stops typing
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);
  const [status, setStatus] = useState(searchParams.get('status') ?? 'all');
  const [upcoming, setUpcoming] = useState(searchParams.get('upcoming') === 'true');
  const [auditFlags, setAuditFlags] = useState<string[]>(() => parseAuditFlagsFromSearch(searchParams));
  const [atRisk, setAtRisk] = useState(
    () => searchParams.get('atRisk') === 'true' && parseAuditFlagsFromSearch(searchParams).length === 0,
  );
  const [publisherId, setPublisherId] = useState(searchParams.get('publisher') ?? '');
  const [seriesId, setSeriesId] = useState(searchParams.get('series') ?? '');
  const [groupId, setGroupId] = useState(searchParams.get('group') ?? '');
  const [upcomingDays, setUpcomingDays] = useState(() => {
    const raw = searchParams.get('upcomingDays');
    const n = raw ? parseInt(raw, 10) : 30;
    return Number.isFinite(n) && n >= 1 && n <= 365 ? n : 30;
  });
  const [hideArchived, setHideArchived] = useState(() => searchParams.get('hideArchived') !== 'false');
  const [advRows, setAdvRows] = useState<AdvUiRow[]>(() => advRowsFromSearchParams(searchParams));
  const [debouncedAdvRows, setDebouncedAdvRows] = useState<AdvUiRow[]>(() => advRowsFromSearchParams(searchParams));
  const [selectedSavedViewId, setSelectedSavedViewId] = useState('');
  const [saveViewName, setSaveViewName] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortCol>(() => {
    const s = searchParams.get('sort');
    if (s === 'name' || s === 'status' || s === 'releaseAt' || s === 'preorderReleaseAt' || s === 'flags') return s;
    return 'releaseAt';
  });
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(() => {
    const dir = searchParams.get('dir');
    if (dir === 'asc' || dir === 'desc') return dir;
    return 'desc'; // default: most recent first
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedAdvRows(advRows), 400);
    return () => clearTimeout(t);
  }, [advRows]);

  const advancedFiltersPayload = useMemo(() => toValidAdvPayload(debouncedAdvRows), [debouncedAdvRows]);

  const saveViewMutation = useMutation({
    mutationFn: ({ name, filtersJson }: { name: string; filtersJson: CatalogSavedViewFilters }) =>
      catalog.views.create(name, filtersJson),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog-views'] });
      setSaveViewName('');
    },
  });

  const deleteViewMutation = useMutation({
    mutationFn: catalog.views.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog-views'] });
      setSelectedSavedViewId('');
    },
  });

  const { data: seriesData } = useQuery({
    queryKey: ['catalog-series'],
    queryFn: catalog.series,
    staleTime: 10 * 60 * 1000, // 10 min — matches backend cache
    gcTime: 20 * 60 * 1000,
  });

  const { data: publishersData } = useQuery({
    queryKey: ['catalog-publishers'],
    queryFn: catalog.publishers,
    staleTime: 10 * 60 * 1000,
    gcTime: 20 * 60 * 1000,
  });

  const { data: groupsData } = useQuery({
    queryKey: ['catalog-groups', seriesId],
    queryFn: () => catalog.groups(seriesId),
    enabled: !!seriesId,
    staleTime: 5 * 60 * 1000,
  });

  const { data: appCfg } = useQuery({
    queryKey: ['app-config'],
    queryFn: appConfig.get,
    staleTime: 60 * 60 * 1000,
  });

  const { data: viewsData } = useQuery({
    queryKey: ['catalog-views'],
    queryFn: catalog.views.list,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const seriesById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of (seriesData?.series ?? []) as Series[]) {
      if (s._id && s.name) m.set(s._id, s.name);
    }
    return m;
  }, [seriesData]);

  const publisherOptions = useMemo(() => {
    const pubs = publishersData?.publishers ?? [];
    return [
      { value: '', label: 'All publishers' },
      ...pubs.map((p) => ({ value: p._id, label: p.value || p.key })),
    ];
  }, [publishersData]);

  const seriesOptions = useMemo(() => {
    const rows = (seriesData?.series ?? []) as Series[];
    return [
      { value: '', label: 'All series' },
      ...rows.filter((s) => s._id).map((s) => ({ value: s._id, label: s.name })),
    ];
  }, [seriesData]);

  const groupOptions = useMemo(() => {
    const rows = groupsData?.groups ?? [];
    return [
      { value: '', label: seriesId ? 'All groups' : 'Select series first' },
      ...rows.map((g) => ({ value: g._id, label: g.name })),
    ];
  }, [groupsData, seriesId]);

  const buildCurrentFiltersJson = useCallback((): CatalogSavedViewFilters => {
    return {
      search: search.trim() ? search : undefined,
      status: status !== 'all' ? status : undefined,
      upcoming: upcoming || undefined,
      upcomingDays: upcoming ? upcomingDays : undefined,
      atRisk: atRisk && auditFlags.length === 0 ? true : undefined,
      auditFlags: auditFlags.length ? auditFlags : undefined,
      publisher: publisherId || undefined,
      series: seriesId || undefined,
      group: groupId || undefined,
      hideArchived: hideArchived ? undefined : false,
      advancedFilters: (() => {
        const p = toValidAdvPayload(advRows);
        return p.length ? p : undefined;
      })(),
      sortBy,
      sortDir,
    };
  }, [
    search,
    status,
    upcoming,
    upcomingDays,
    atRisk,
    auditFlags,
    publisherId,
    seriesId,
    groupId,
    hideArchived,
    advRows,
    sortBy,
    sortDir,
  ]);

  const applySavedFilters = useCallback((f: CatalogSavedViewFilters) => {
    setSearchInput(f.search ?? '');
    setSearch(f.search ?? '');
    setStatus(f.status ?? 'all');
    setUpcoming(!!f.upcoming);
    setUpcomingDays(
      f.upcomingDays != null && Number.isFinite(f.upcomingDays) ? Math.min(365, Math.max(1, f.upcomingDays)) : 30,
    );
    const savedFlags = f.auditFlags?.filter((id) => id in AT_RISK_LABELS) ?? [];
    setAuditFlags(savedFlags);
    setAtRisk(!!f.atRisk && savedFlags.length === 0);
    setPublisherId(f.publisher ?? '');
    setSeriesId(f.series ?? '');
    setGroupId(f.group ?? '');
    setHideArchived(f.hideArchived !== false);
    const af = f.advancedFilters;
    if (Array.isArray(af) && af.length) {
      setAdvRows(
        af.filter((x) => x && CATALOG_ADV_FIELD_ID_SET.has(String(x.field))).map((x) =>
          createAdvRow({
            field: String(x.field),
            op: x.op as CatalogAdvFilterOp,
            value: typeof x.value === 'string' ? x.value : String(x.value ?? ''),
          }),
        ),
      );
    } else if (typeof f.missingField === 'string' && f.missingField.trim()) {
      const mf = f.missingField.trim();
      const field = mf === 'series' ? 'series_id' : mf === 'group' ? 'group_id' : mf;
      setAdvRows(
        CATALOG_ADV_FIELD_ID_SET.has(field) ? [createAdvRow({ field, op: 'empty', value: '' })] : [],
      );
    } else {
      setAdvRows([]);
    }
    const sb = f.sortBy;
    if (sb === 'name' || sb === 'status' || sb === 'releaseAt' || sb === 'preorderReleaseAt' || sb === 'flags') {
      setSortBy(sb);
    }
    const d = f.sortDir;
    if (d === 'asc' || d === 'desc') setSortDir(d);
    setPage(1);
  }, []);

  const queryParams = useMemo(
    () => ({
      search: search || undefined,
      status: status !== 'all' ? status : undefined,
      upcoming: upcoming || undefined,
      upcomingDays: upcoming ? upcomingDays : undefined,
      atRisk: atRisk && auditFlags.length === 0 ? true : undefined,
      auditFlags: auditFlags.length ? auditFlags : undefined,
      publisher: publisherId || undefined,
      series: seriesId || undefined,
      group: groupId || undefined,
      hideArchived: hideArchived ? undefined : false,
      advancedFilters: advancedFiltersPayload.length ? advancedFiltersPayload : undefined,
      page,
      sortBy: sortBy === 'flags' ? 'releaseAt' : sortBy,
      sortDir,
    }),
    [
      search,
      status,
      upcoming,
      upcomingDays,
      atRisk,
      auditFlags,
      publisherId,
      seriesId,
      groupId,
      hideArchived,
      advancedFiltersPayload,
      page,
      sortBy,
      sortDir,
    ],
  );

  const exportMatchesCurrentFilters = useCallback(async () => {
    try {
      setExportBusy(true);
      setInlineError(null);
      await catalog.exportItemsCsv({ ...queryParams, page: undefined, limit: undefined });
    } catch (e) {
      setInlineError(e instanceof ApiError ? e.message : 'Export failed.');
    } finally {
      setExportBusy(false);
    }
  }, [queryParams]);

  const toggleAuditFlag = useCallback((flagId: string) => {
    setAtRisk(false);
    setAuditFlags((prev) =>
      prev.includes(flagId) ? prev.filter((f) => f !== flagId) : [...prev, flagId],
    );
    setPage(1);
  }, []);

  const clearAuditFilters = useCallback(() => {
    setAuditFlags([]);
    setAtRisk(false);
    setPage(1);
  }, []);

  const clearAllFilters = useCallback(() => {
    setSearchInput('');
    setSearch('');
    setStatus('all');
    setUpcoming(false);
    setUpcomingDays(30);
    setAuditFlags([]);
    setAtRisk(false);
    setPublisherId('');
    setSeriesId('');
    setGroupId('');
    setHideArchived(true);
    setAdvRows([]);
    setSelectedSavedViewId('');
    setSortBy('releaseAt');
    setSortDir('desc');
    setPage(1);
  }, []);

  const handleBulkAction = useCallback(async (patch: { status?: string; isArchived?: boolean }) => {
    if (selectedIds.size === 0) return;
    const ids = [...selectedIds];
    setBulkBusy(true);
    setInlineError(null);
    try {
      const result = await catalogAdmin.bulkPatchItems(ids, patch);
      if (result.failed.length > 0) {
        setInlineError(`${result.succeeded.length} succeeded, ${result.failed.length} failed: ${result.failed.map(f => f.error).join(', ')}`);
      }
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['catalog-items'] });
    } catch (e) {
      setInlineError(e instanceof ApiError ? e.message : 'Bulk action failed');
    } finally {
      setBulkBusy(false);
    }
  }, [selectedIds, queryClient]);

  const { data, isLoading, isError: itemsError } = useQuery({
    queryKey: ['catalog-items', queryParams],
    queryFn: () => catalog.items(queryParams),
    placeholderData: (prev) => prev,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const handleSort = useCallback((col: SortCol) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      const defaultDesc = col === 'releaseAt' || col === 'preorderReleaseAt' || col === 'flags';
      setSortDir(defaultDesc ? 'desc' : 'asc');
      setSortBy(col);
    }
  }, [sortBy]);

  useEffect(() => {
    setPage(1);
  }, [search, status, upcoming, upcomingDays, atRisk, auditFlags, publisherId, seriesId, groupId, hideArchived, advRows]);

  // Reset group when series changes (groups are series-scoped)
  useEffect(() => {
    setGroupId('');
  }, [seriesId]);

  useEffect(() => {
    const p: Record<string, string> = {};
    if (search) p.search = search;
    if (status !== 'all') p.status = status;
    if (upcoming) {
      p.upcoming = 'true';
      if (upcomingDays !== 30) p.upcomingDays = String(upcomingDays);
    }
    if (auditFlags.length) p.flags = auditFlags.join(',');
    else if (atRisk) p.atRisk = 'true';
    if (publisherId) p.publisher = publisherId;
    if (seriesId) p.series = seriesId;
    if (groupId) p.group = groupId;
    if (!hideArchived) p.hideArchived = 'false';
    if (advancedFiltersPayload.length) {
      p.adv = encodeURIComponent(JSON.stringify(advancedFiltersPayload));
    }
    if (sortBy !== 'releaseAt') p.sort = sortBy;
    if (sortDir !== 'asc') p.dir = sortDir;
    setSearchParams(p, { replace: true });
  }, [
    search,
    status,
    upcoming,
    upcomingDays,
    atRisk,
    auditFlags,
    publisherId,
    seriesId,
    groupId,
    hideArchived,
    advancedFiltersPayload,
    sortBy,
    sortDir,
    setSearchParams,
  ]);

  const rawItems: CatalogItem[] = (data as any)?.items ?? [];
  const total: number = (data as any)?.total ?? 0;
  const totalPages = Math.ceil(total / 50);

  // Backend handles sort for DB columns. Client-side only for "flags" (computed field).
  const items = useMemo(() => {
    if (sortBy !== 'flags') return rawItems;
    const sorted = [...rawItems];
    const dir = sortDir === 'desc' ? -1 : 1;
    sorted.sort((a, b) => {
      const af = (a.atRiskFlags ?? []).length;
      const bf = (b.atRiskFlags ?? []).length;
      return dir * (af - bf);
    });
    return sorted;
  }, [rawItems, sortBy, sortDir]);

  /** `IGLU_ADMIN_ORIGIN` from GET /api/config unless overridden by VITE_IGLU_ADMIN_ORIGIN (local). */
  const ADMIN_URL = useMemo(() => {
    const vite = import.meta.env.VITE_IGLU_ADMIN_ORIGIN?.replace(/\/$/, '');
    if (vite) return vite;
    const injected =
      typeof window !== 'undefined'
        ? (window as unknown as { __VITE_ADMIN_URL__?: string }).__VITE_ADMIN_URL__
        : '';
    if (injected) return injected.replace(/\/$/, '');
    return appCfg?.igluAdminOrigin?.replace(/\/$/, '') ?? '';
  }, [appCfg]);

  const presetCls = (active: boolean) =>
    `px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
      active
        ? 'bg-brand-50 border-brand-500 text-brand-800'
        : 'border-gray-200 text-gray-600 hover:border-brand-300 hover:bg-gray-50'
    }`;

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Catalog Browser</h1>
          <p className="text-gray-500 text-sm mt-1">{total} items found</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            type="button"
            variant="secondary"
            onClick={exportMatchesCurrentFilters}
            disabled={exportBusy}
            title="Download spreadsheet (current filters; full list batched, max 500k rows)"
          >
            <Download size={14} className={exportBusy ? 'opacity-50' : ''} />
            {exportBusy ? 'Exporting…' : 'Export CSV'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            title="Sync catalog from Iglu"
          >
            <RefreshCw size={14} className={syncMutation.isPending ? 'animate-spin' : ''} />
            {syncMutation.isPending ? 'Syncing…' : 'Refresh'}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="min-w-[12rem] flex-1 max-w-xs">
          <p className="text-xs text-gray-500 mb-1">Saved view</p>
          <SearchableSelect
            value={selectedSavedViewId}
            onChange={(id) => {
              setSelectedSavedViewId(id);
              const row = viewsData?.views?.find((v) => v.id === id);
              if (!row?.filtersJson || typeof row.filtersJson !== 'object') return;
              applySavedFilters(row.filtersJson as CatalogSavedViewFilters);
            }}
            options={[
              { value: '', label: '— None —' },
              ...(viewsData?.views ?? []).map((v) => ({ value: v.id, label: v.name })),
            ]}
            placeholder="Load…"
            allowClear={false}
          />
        </div>
        <div className="min-w-[10rem] flex-1 max-w-xs">
          <p className="text-xs text-gray-500 mb-1">Save as</p>
          <input
            type="text"
            value={saveViewName}
            onChange={(e) => setSaveViewName(e.target.value)}
            placeholder="View name"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="shrink-0"
          loading={saveViewMutation.isPending}
          disabled={!saveViewName.trim() || saveViewMutation.isPending}
          onClick={() =>
            saveViewMutation.mutate({ name: saveViewName.trim(), filtersJson: buildCurrentFiltersJson() })
          }
        >
          <Bookmark size={14} /> Save
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="shrink-0"
          loading={deleteViewMutation.isPending}
          disabled={!selectedSavedViewId || deleteViewMutation.isPending}
          onClick={() => {
            if (!selectedSavedViewId) return;
            if (!window.confirm('Delete this saved view?')) return;
            deleteViewMutation.mutate(selectedSavedViewId);
          }}
        >
          <Trash2 size={14} /> Delete
        </Button>
      </div>

      <div className="flex items-center gap-4 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-64">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search title, ISBN, series…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>

        <div className="min-w-[11rem]">
          <SearchableSelect
            value={status}
            onChange={setStatus}
            options={CATALOG_STATUS_OPTIONS}
            placeholder="Status"
            allowClear={false}
          />
        </div>

        <button
          type="button"
          onClick={() => setUpcoming(!upcoming)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
            upcoming ? 'bg-brand-600 border-brand-600 text-white' : 'border-gray-300 text-gray-700 hover:border-brand-400'
          }`}
        >
          <CalendarDays size={14} /> Upcoming
        </button>

        <div className="min-w-[11rem]">
          <SearchableSelect
            value={publisherId}
            onChange={setPublisherId}
            options={publisherOptions}
            placeholder="Publisher"
            allowClear={false}
          />
        </div>
        <div className="min-w-[11rem]">
          <SearchableSelect
            value={seriesId}
            onChange={setSeriesId}
            options={seriesOptions}
            placeholder="Series"
            allowClear={false}
          />
        </div>
        <div className="min-w-[11rem]">
          <SearchableSelect
            value={groupId}
            onChange={setGroupId}
            options={groupOptions}
            placeholder="Group"
            allowClear={false}
            disabled={!seriesId}
          />
        </div>

        {upcoming && (
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <span className="whitespace-nowrap">Days</span>
            <input
              type="number"
              min={1}
              max={365}
              value={upcomingDays}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                if (Number.isFinite(n) && n >= 1 && n <= 365) setUpcomingDays(n);
              }}
              className="w-16 border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </label>
        )}
        <button
          type="button"
          onClick={() => { setAtRisk((v) => !v); setAuditFlags([]); setPage(1); }}
          className={`shrink-0 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
            atRisk
              ? 'bg-orange-100 border-orange-300 text-orange-800 hover:bg-orange-200'
              : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
          }`}
        >
          At Risk
        </button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="shrink-0"
          onClick={clearAllFilters}
          title="Reset search, status, dates, publishers, audit flags, fine-tune rules, and sort"
        >
          <FilterX size={14} /> Clear filters
        </Button>
      </div>

      <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50/80 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Fine-tune</span>
          <div className="flex items-center gap-2">
            {advRows.length > 0 && (
              <button
                type="button"
                className="text-xs text-gray-500 hover:text-gray-800 px-2 py-1"
                onClick={() => {
                  setAdvRows([]);
                  setPage(1);
                }}
              >
                Clear all
              </button>
            )}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="shrink-0"
              onClick={() => {
                setAdvRows((prev) => [...prev, createAdvRow()]);
                setPage(1);
              }}
            >
              <Plus size={14} /> Add rule
            </Button>
          </div>
        </div>
        {advRows.length === 0 ? (
          <p className="text-sm text-gray-500">Add one or more rules. Items must match every rule.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {advRows.map((row) => (
              <div key={row.key} className="flex flex-wrap items-end gap-2 md:gap-3">
                <div className="min-w-[10rem] flex-1 max-w-[14rem]">
                  <p className="text-xs text-gray-500 mb-1">Pick something</p>
                  <SearchableSelect
                    value={row.field}
                    onChange={(field) => {
                      setAdvRows((prev) =>
                        prev.map((r) => (r.key === row.key ? { ...r, field } : r)),
                      );
                      setPage(1);
                    }}
                    options={[
                      { value: '', label: 'Pick…' },
                      ...CATALOG_ADV_FIELD_OPTIONS.map((o) => ({ value: o.id, label: o.label })),
                    ]}
                    placeholder="Pick…"
                    allowClear={false}
                  />
                </div>
                <div className="min-w-[9rem] flex-1 max-w-[12rem]">
                  <p className="text-xs text-gray-500 mb-1">How to match</p>
                  <SearchableSelect
                    value={row.op}
                    onChange={(op) => {
                      setAdvRows((prev) =>
                        prev.map((r) => (r.key === row.key ? { ...r, op: op as CatalogAdvFilterOp } : r)),
                      );
                      setPage(1);
                    }}
                    options={CATALOG_ADV_OP_OPTIONS.map((o) => ({ value: o.id, label: o.label }))}
                    placeholder="How…"
                    allowClear={false}
                  />
                </div>
                <div className="min-w-[8rem] flex-[2] max-w-md">
                  <p className="text-xs text-gray-500 mb-1">Match this</p>
                  {(row.op === 'eq' || row.op === 'neq' || row.op === 'contains') && CATALOG_ADV_ENUM_OPTIONS[row.field] ? (
                    <SearchableSelect
                      value={row.value}
                      onChange={(value) => {
                        setAdvRows((prev) => prev.map((r) => (r.key === row.key ? { ...r, value } : r)));
                      }}
                      options={CATALOG_ADV_ENUM_OPTIONS[row.field]}
                      placeholder="Pick a value…"
                      allowClear={false}
                    />
                  ) : (
                    <input
                      type="text"
                      value={row.value}
                      disabled={row.op === 'empty' || row.op === 'not_empty'}
                      onChange={(e) => {
                        const value = e.target.value;
                        setAdvRows((prev) => prev.map((r) => (r.key === row.key ? { ...r, value } : r)));
                      }}
                      placeholder={
                        row.op === 'empty' || row.op === 'not_empty'
                          ? '—'
                          : 'Words, yes / no, or a number'
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-gray-100 disabled:text-gray-400"
                    />
                  )}
                </div>
                <button
                  type="button"
                  aria-label="Remove this rule"
                  className="p-2 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-800 shrink-0 mb-0.5"
                  onClick={() => {
                    setAdvRows((prev) => prev.filter((r) => r.key !== row.key));
                    setPage(1);
                  }}
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-1 mr-1 shrink-0">
          <AlertTriangle size={14} aria-hidden /> Audit flags
        </span>
        {AUDIT_FLAG_IDS.map((flagId) => {
          const info = AT_RISK_LABELS[flagId];
          const active = auditFlags.includes(flagId);
          return (
            <button
              key={flagId}
              type="button"
              onClick={() => toggleAuditFlag(flagId)}
              className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                active
                  ? `${info.color} border-transparent ring-1 ring-inset ring-black/5`
                  : 'border-gray-200 text-gray-600 hover:border-gray-300 bg-white'
              }`}
            >
              {info.label}
            </button>
          );
        })}
        {(auditFlags.length > 0 || atRisk) && (
          <button
            type="button"
            onClick={clearAuditFilters}
            className="text-xs text-gray-500 hover:text-gray-800 px-2 py-1"
          >
            Clear flags
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-1 mr-1">
          <Layers size={14} aria-hidden /> Presets
        </span>
        <label className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 cursor-pointer hover:border-gray-300">
          <input
            type="checkbox"
            checked={hideArchived}
            onChange={(e) => {
              setHideArchived(e.target.checked);
              setPage(1);
            }}
            className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          Hide archived
        </label>
        <button
          type="button"
          className={presetCls(upcoming && upcomingDays === 3 && !atRisk && auditFlags.length === 0)}
          onClick={() => {
            setUpcoming(true);
            setUpcomingDays(3);
            setAuditFlags([]);
            setAtRisk(false);
            setPage(1);
          }}
        >
          Next 3 days
        </button>
        <button
          type="button"
          className={presetCls(upcoming && upcomingDays === 7 && !atRisk && auditFlags.length === 0)}
          onClick={() => {
            setUpcoming(true);
            setUpcomingDays(7);
            setAuditFlags([]);
            setAtRisk(false);
            setPage(1);
          }}
        >
          Next 7 days
        </button>
        <button
          type="button"
          className={presetCls(upcoming && upcomingDays === 14 && !atRisk && auditFlags.length === 0)}
          onClick={() => {
            setUpcoming(true);
            setUpcomingDays(14);
            setAuditFlags([]);
            setAtRisk(false);
            setPage(1);
          }}
        >
          Next 14 days
        </button>
        <button
          type="button"
          className={presetCls(upcoming && upcomingDays === 30 && !atRisk && auditFlags.length === 0)}
          onClick={() => {
            setUpcoming(true);
            setUpcomingDays(30);
            setAuditFlags([]);
            setAtRisk(false);
            setPage(1);
          }}
        >
          Next 30 days
        </button>
      </div>

      {inlineError && (
        <div className="mb-4 flex items-start gap-2 text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span className="flex-1">{inlineError}</span>
          <button onClick={() => setInlineError(null)} className="text-red-400 hover:text-red-600 shrink-0">×</button>
        </div>
      )}

      <Card>
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="animate-spin mr-2" /> Loading catalog…
          </div>
        ) : itemsError ? (
          <div className="flex items-center gap-2 text-red-600 py-12 px-6 text-sm">
            <AlertTriangle size={16} /> Failed to load catalog items — check your connection and try refreshing.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  {me?.enableEdit && (
                    <th className="px-3 py-3 w-10">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                        checked={items.length > 0 && items.every(i => selectedIds.has(i._id))}
                        onChange={e => {
                          if (e.target.checked) setSelectedIds(new Set(items.map(i => i._id)));
                          else setSelectedIds(new Set());
                        }}
                      />
                    </th>
                  )}
                  <SortTh label="Title" column="name" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortTh label="Status" column="status" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortTh label="Release" column="releaseAt" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortTh
                    label="Preorder"
                    column="preorderReleaseAt"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                  <SortTh label="Flags" column="flags" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <th className="px-2 py-3 w-14 min-w-[3.5rem]" aria-label="Open in admin" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {items.map((item) => (
                  <tr
                    key={item._id}
                    onClick={() => navigate(`/catalog/${item._id}`)}
                    className={`hover:bg-gray-50 cursor-pointer transition-colors ${selectedIds.has(item._id) ? 'bg-brand-50' : ''}`}
                  >
                    {me?.enableEdit && (
                      <td className="px-3 py-3" onClick={e => { e.stopPropagation(); setSelectedIds(prev => { const n = new Set(prev); n.has(item._id) ? n.delete(item._id) : n.add(item._id); return n; }); }}>
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                          checked={selectedIds.has(item._id)}
                          onChange={() => {}}
                        />
                      </td>
                    )}
                    <td className="px-4 py-3 pl-2">
                      <div className="flex items-center gap-3">
                        {item.image && item.image !== SENTINEL ? (
                          <img
                            src={String(item.image)}
                            alt=""
                            className="w-10 h-10 rounded object-cover flex-shrink-0 bg-gray-100"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-10 h-10 rounded bg-gray-100 flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate max-w-xs">{item.name}</p>
                          <p className="text-xs text-gray-500 truncate max-w-xs mt-0.5">{seriesLabel(item, seriesById)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={statusBadge(item.status ?? '')}>{item.status ?? '—'}</Badge>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{formatItemDate(item.releaseAt)}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{formatItemDate(item.preorderReleaseAt)}</td>
                    <td className="px-4 py-3">
                      <AtRiskBadge flags={item.atRiskFlags ?? []} />
                    </td>
                    <td className="px-2 py-2 align-middle w-14 min-w-[3.5rem]">
                      {ADMIN_URL ? (
                        <a
                          href={`${ADMIN_URL}/items?id=${item._id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center justify-center min-h-11 min-w-11 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                          title="Open in Iglu admin"
                        >
                          <ExternalLink size={20} strokeWidth={2} aria-hidden />
                        </a>
                      ) : (
                        <span
                          className="inline-flex items-center justify-center min-h-11 min-w-11 text-gray-200"
                          title="Set VITE_IGLU_ADMIN_ORIGIN for admin links"
                        >
                          <ExternalLink size={20} strokeWidth={2} aria-hidden />
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center text-gray-400">
                      No items match the current filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100">
            <p className="text-sm text-gray-500">
              Page {page} of {totalPages} · {total} items
            </p>
            <div className="flex items-center gap-2">
              <Button variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                <ChevronLeft size={14} />
              </Button>
              <Button variant="secondary" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Bulk action bar */}
      {me?.enableEdit && selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-gray-900 text-white rounded-xl shadow-2xl px-5 py-3 text-sm">
          <span className="font-medium text-gray-300">{selectedIds.size} selected</span>
          <span className="w-px h-5 bg-gray-700" />
          <span className="text-xs text-gray-400 mr-1">Set status:</span>
          {['draft', 'published', 'archived'].map(s => (
            <Button
              key={s}
              variant="secondary"
              size="sm"
              disabled={bulkBusy}
              onClick={() => handleBulkAction({ status: s })}
              className="!bg-gray-800 !border-gray-700 !text-white hover:!bg-gray-700 capitalize"
            >
              {s}
            </Button>
          ))}
          <span className="w-px h-5 bg-gray-700" />
          <Button
            variant="secondary"
            size="sm"
            disabled={bulkBusy}
            onClick={() => handleBulkAction({ isArchived: true })}
            className="!bg-gray-800 !border-gray-700 !text-white hover:!bg-gray-700"
          >
            Archive
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={bulkBusy}
            onClick={() => handleBulkAction({ isArchived: false })}
            className="!bg-gray-800 !border-gray-700 !text-white hover:!bg-gray-700"
          >
            Unarchive
          </Button>
          <span className="w-px h-5 bg-gray-700" />
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            className="text-gray-400 hover:text-white transition-colors p-1"
            disabled={bulkBusy}
          >
            <X size={16} />
          </button>
          {bulkBusy && <Loader2 size={16} className="animate-spin text-gray-400" />}
        </div>
      )}
    </div>
  );
}

function CalendarDays({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}
